import { z } from 'zod';
import { TicketDocumentosSchema } from './ticket.js';

/**
 * Full Plan Matriz schema (database record representation).
 */
export const PlanMatrizSchema = z.object({
  id: z.number().int(),
  nombre: z.string().min(1),
  codigo: z.string().nullable(),
  subcontratoId: z.number().int(),
  ambitoId: z.number().int(),
  ubicacionId: z.number().int().nullable(),
  empresaId: z.number().int().nullable(),
  pautasId: z.number().int().nullable(),
  turnosId: z.number().int().nullable(),
  tipoMantenimientoId: z.number().int().nullable(),
  frecuencia: z.string().nullable(),
  tiempoTrabajo: z.number().nullable(),
  tiempoViaje: z.number().nullable(),
  activo: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PlanMatriz = z.infer<typeof PlanMatrizSchema>;

/**
 * Create Plan Matriz input (POST /api/v1/plan-matriz).
 */
export const CreatePlanMatrizSchema = z.object({
  nombre: z.string().min(1).max(500),
  codigo: z.string().max(50).optional(),
  subcontratoId: z.number().int().positive(),
  ambitoId: z.number().int().positive(),
  ubicacionId: z.number().int().positive().optional(),
  empresaId: z.number().int().positive().optional(),
  pautasId: z.number().int().positive().optional(),
  turnosId: z.number().int().positive().optional(),
  tipoMantenimientoId: z.number().int().positive().optional(),
  catalogoecoId: z.number().int().positive().nullable().optional(),
  tipoEquipoId: z.number().int().positive().nullable().optional(),
  frecuencia: z.string().max(100).optional(),
  tiempoTrabajo: z.number().min(0).optional(),
  tiempoViaje: z.number().min(0).optional(),
});

export type CreatePlanMatriz = z.infer<typeof CreatePlanMatrizSchema>;

/**
 * Update Plan Matriz input (PUT /api/v1/plan-matriz/:id - partial).
 */
export const UpdatePlanMatrizSchema = CreatePlanMatrizSchema.partial();

export type UpdatePlanMatriz = z.infer<typeof UpdatePlanMatrizSchema>;

/**
 * Search query params for Plan Matriz fuzzy search.
 */
export const PlanMatrizSearchSchema = z.object({
  q: z.string().min(1).max(200),
  ambitoId: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export type PlanMatrizSearch = z.infer<typeof PlanMatrizSearchSchema>;

/**
 * List query params for Plan Matriz pagination.
 */
export const PlanMatrizListSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  ambitoId: z.coerce.number().int().positive().optional(),
  subcontratoId: z.coerce.number().int().positive().optional(),
  activo: z.coerce.boolean().optional(),
  search: z.string().optional(),
});

export type PlanMatrizList = z.infer<typeof PlanMatrizListSchema>;

/**
 * Plan Matriz Alias schema (database record - nrwmpms alias cache).
 */
export const PlanMatrizAliasSchema = z.object({
  id: z.number().int(),
  nombre: z.string().min(1),
  planMatrizId: z.number().int(),
  pautasId: z.number().int().nullable(),
});

export type PlanMatrizAlias = z.infer<typeof PlanMatrizAliasSchema>;

/**
 * Create Plan Matriz Alias input.
 */
export const CreatePlanMatrizAliasSchema = z.object({
  nombre: z.string().min(1).max(500),
});

export type CreatePlanMatrizAlias = z.infer<typeof CreatePlanMatrizAliasSchema>;

// =============================================================================
// Vista Mensual (Monthly Calendar View)
// =============================================================================

/**
 * Query params for GET /plan-matriz/monthly/:year/:month
 */
export const MonthlyQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive().optional(),
  subcontratoId: z.coerce.number().int().positive().optional(),
  // Equipment filter (tipo_equipo → catalogoeco), same as the ticket listados.
  tipoEquipoId: z.coerce.number().int().positive().optional(),
  catalogoecoId: z.coerce.number().int().positive().optional(),
});

export type MonthlyQuery = z.infer<typeof MonthlyQuerySchema>;

/**
 * A single ticket entry as returned in the monthly view.
 */
export const MonthlyTicketItemSchema = z.object({
  id: z.number().int(),
  correlativo: z.string(),
  estadoOt: z.enum([
    'PROGRAMADO',
    'INICIADO',
    'NO_EJECUTADO',
    'EJECUTADO',
    'APROBADO',
    'RECHAZADO',
    'REPROGRAMADO',
  ]),
  tipoOt: z.object({ id: z.number().int(), nombre: z.string() }).nullable(),
  planMatriz: z.object({ id: z.number().int(), nombre: z.string() }).nullable(),
  // Activity name for NPL tickets, which have no planMatriz. The card/tooltip label
  // is `planMatriz?.nombre ?? servicio` (never the pauta) — same convention as the
  // mantenedor list and miniapp. Scalar on `ot`, so the calendar select must add it.
  servicio: z.string().nullable(),
  ubicacion: z.object({ nombre: z.string() }).nullable(),
  fecha: z.string(), // ISO date string YYYY-MM-DD
  prioridad: z.boolean(),
  // Real document availability, so the card can show a certificate badge that
  // reflects the DB instead of guessing from `estadoOt`. Shared with the Lista
  // tab; see TicketDocumentosSchema for why the estado-based heuristic lies.
  documentos: TicketDocumentosSchema,
});

export type MonthlyTicketItem = z.infer<typeof MonthlyTicketItemSchema>;

/**
 * Reusable summary block: total count + per-status breakdown.
 */
const SummarySchema = z.object({
  total: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
});

/**
 * Response shape for GET /plan-matriz/monthly/:year/:month
 */
export const MonthlyViewResponseSchema = z.object({
  month: z.string(), // "YYYY-MM"
  days: z.record(z.string(), z.array(MonthlyTicketItemSchema)),
  summary: SummarySchema,
});

export type MonthlyViewResponse = z.infer<typeof MonthlyViewResponseSchema>;

/**
 * Response shape for GET /plan-matriz/weekly/:date
 *
 * Weeks run Wed→Tue. `weekStart` is always a Wednesday (YYYY-MM-DD),
 * `weekEnd` the following Tuesday (inclusive). `turno` is the rotation
 * label A/B for the entire week (alternating from a fixed reference).
 */
export const WeeklyViewResponseSchema = z.object({
  weekStart: z.string(), // YYYY-MM-DD (Wed)
  weekEnd: z.string(),   // YYYY-MM-DD (Tue, inclusive)
  turno: z.enum(['A', 'B']),
  days: z.record(z.string(), z.array(MonthlyTicketItemSchema)),
  summary: SummarySchema,
});

export type WeeklyViewResponse = z.infer<typeof WeeklyViewResponseSchema>;

/**
 * Response shape for GET /plan-matriz/daily/:date
 */
export const DailyViewResponseSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  turno: z.enum(['A', 'B']),
  tickets: z.array(MonthlyTicketItemSchema),
  summary: SummarySchema,
});

export type DailyViewResponse = z.infer<typeof DailyViewResponseSchema>;

/**
 * Validates a YYYY-MM-DD string for both syntax AND semantic correctness
 * (rejects rolloverable dates like 2026-02-31 or 2025-02-29 in non-leap years).
 * Used in request bodies that target specific calendar dates.
 */
const dateStringSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Formato: YYYY-MM-DD')
  .refine((s) => {
    const parts = s.split('-');
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
  }, 'Fecha inexistente (verifique el día)');

/**
 * Request body for PUT /plan-matriz/reschedule/:ticketId
 */
export const RescheduleTicketSchema = z.object({
  newDate: dateStringSchema,
});

export type RescheduleTicketInput = z.infer<typeof RescheduleTicketSchema>;

/**
 * Rescheduled ticket response (minimal shape returned after update).
 */
export const RescheduledTicketSchema = z.object({
  id: z.number().int(),
  correlativo: z.string(),
  estadoOt: z.enum([
    'PROGRAMADO',
    'INICIADO',
    'NO_EJECUTADO',
    'EJECUTADO',
    'APROBADO',
    'RECHAZADO',
    'REPROGRAMADO',
  ]),
  fechaProgramada: z.string().nullable(),
  updatedAt: z.string(),
});

export type RescheduledTicket = z.infer<typeof RescheduledTicketSchema>;
